import { addDays, format, getDay, getHours, getMinutes, parseISO, startOfWeek } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { collection, getDocs, query, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';

// --- WeeklyCalendar Component ---

function WeeklyCalendar({ lastSyncTime }) { // Accept lastSyncTime prop to trigger refresh
  const { globalUser } = useAuth();
  const userId = globalUser?.uid;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false); // For initial Firestore load
  const [error, setError] = useState(null);
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(toZonedTime(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone), { weekStartsOn: 0 })
  );
  const [timeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Fetch events from Firestore (memoized)
  const fetchEventsFromFirestore = useCallback(async () => {
    if (!userId) {
      setEvents([]);
      setError("User not available.");
      return;
    }

    console.log(`Workspaceing Firestore events for week starting: ${format(weekStart, 'yyyy-MM-dd')}`);
    setLoading(true);
    setError(null); // Clear previous Firestore errors
    // Sync errors are handled separately
    try {
      const start = weekStart;
      const end = addDays(weekStart, 7); // Firestore query needs dates, not datetime

      const eventsRef = collection(db, 'lawyers', userId, 'google_events');

      const q = query(
        eventsRef,
        where('date', '>=', format(start, 'yyyy-MM-dd')),
        where('date', '<', format(end, 'yyyy-MM-dd')) // Fetch events starting within the week
      );
      const snapshot = await getDocs(q);
      const fetchedEvents = snapshot.docs.map(doc => ({
        // Ensure the data structure matches what renderEvents expects
        id: doc.id,
        summary: doc.data().summary,
        start: doc.data().start, // Assuming 'start' object with 'dateTime' is stored
        end: doc.data().end,     // Assuming 'end' object with 'dateTime' is stored
        description: doc.data().description,
        // Add other fields if needed by renderEvents or event handlers
      }));

      console.log(`Workspaceed ${fetchedEvents.length} events from Firestore for the week.`);
      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching events from Firestore:', err);
      setError('Failed to load events from Firestore. Check console for details.');
      setEvents([]); // Clear events on error
    } finally {
      setLoading(false);
    }
  }, [userId, weekStart]); // Dependencies for refetching from Firestore


  // Effect to fetch events when week, user, or lastSyncTime changes
  useEffect(() => {
    if (userId) {
      fetchEventsFromFirestore();
    } else {
      setEvents([]); // Clear events if user logs out
      setError("Please sign in to view events.");
    }
  }, [userId, weekStart, lastSyncTime, fetchEventsFromFirestore]); // Refresh when lastSyncTime changes

  // Navigation handlers
  const goToPreviousWeek = () => setWeekStart(prev => addDays(prev, -7));
  const goToNextWeek = () => setWeekStart(prev => addDays(prev, 7));
  const goToCurrentWeek = () =>
    setWeekStart(startOfWeek(toZonedTime(new Date(), timeZone), { weekStartsOn: 0 }));

  // Event rendering logic (ensure it uses the fields correctly)
  const getEventColorClass = (eventTitle) => {
    const eventTypes = {
      'Document sub': 'bg-blue-600',
      'Ad\'s meeting': 'bg-green-500',
      'Dinner time': 'bg-orange-400',
      'Movie': 'bg-orange-400',
      'Gym': 'bg-orange-400',
      'Function': 'bg-green-500',
    };
    return eventTypes[eventTitle] || 'bg-blue-600'; // Default to blue like the nav buttons
  };

  const formatHourDisplay = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  // Adjusted to use the correct timezone logic and data structure
  const parseEventDate = (dateTimeString) => {
    // dateTimeString is expected to be in ISO format (e.g., "2024-04-15T10:00:00-07:00")
    // parseISO handles the offset correctly. toZonedTime converts it to the *local* browser timezone for calculations.
    try {
      return toZonedTime(parseISO(dateTimeString), timeZone);
    } catch (e) {
      console.error(`Error parsing date: ${dateTimeString}`, e);
      return null; // Handle invalid date strings gracefully
    }
  };


  const renderEvents = () => {
    // Ensure events array exists and has items before mapping
    if (!Array.isArray(events) || !events.length) return null;

    const rowHeight = 40; // Assuming grid row height is 40px

    return events.map(event => {
      // **Crucial Check:** Ensure start and end dateTime exist
      if (!event?.start?.dateTime || !event?.end?.dateTime) {
        console.warn(`Skipping rendering event with missing start/end dateTime: ${event?.id} - ${event?.summary}`);
        return null;
      }

      const start = parseEventDate(event.start.dateTime);
      const end = parseEventDate(event.end.dateTime);

      // Handle potential parsing errors
      if (!start || !end || start >= end) {
        console.warn(`Skipping rendering event with invalid dates: ${event.id} - ${event.summary}`);
        return null;
      }

      // Check if the event *actually* occurs on any day within the current week view.
      // This is important if the Firestore query fetched events starting before the week.
      const eventFallsInWeek = weekDays.some(day =>
        start < addDays(day, 1) && end > day // Simple overlap check
      );

      if (!eventFallsInWeek) {
        // console.log(`Event ${event.summary} (${format(start, 'PPp')}) does not fall in the current week view.`);
        return null; // Don't render events outside the current view
      }


      // Calculate grid positioning based on the *local* time representation
      // Use the start time that's been converted to the user's local timezone
      const localStartDay = getDay(start); // Sunday = 0, Saturday = 6
      const localStartHour = getHours(start);
      const localStartMinute = getMinutes(start);

      // Calculate duration in minutes
      const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

      // Adjust for grid layout (column 1 is time, 2-8 are days Sun-Sat)
      const gridColumn = localStartDay + 2;
      // Adjust for grid layout (row 1 is header, 2-25 are hours 0-23)
      const gridRow = localStartHour + 2;

      // Calculate precise positioning within the hour cell
      const startOffset = (localStartMinute / 60) * rowHeight; // Offset from the top of the hour row
      const totalHeight = Math.max((durationMinutes / 60) * rowHeight, 10); // Min height to be visible

      // Skip rendering if event doesn't belong to any column in the current view
      // (This check might be redundant due to eventFallsInWeek check, but good for safety)
      if (gridColumn < 2 || gridColumn > 8) {
        return null;
      }


      // Format times
      const startTime = format(start, 'h:mm a');
      const endTime = format(end, 'h:mm a');
      const timeRange = `${format(start, 'h:mm')} - ${format(end, 'h:mm a')}`;

      return (
        <div
          key={event.id}
          style={{
            gridColumn: gridColumn,
            gridRow: gridRow,
            position: 'relative',
          }}
        >
          <div
            className={`absolute rounded-md px-2 py-1 text-xs text-white shadow-lg overflow-hidden cursor-pointer hover:brightness-110 transition-all ${getEventColorClass(event.summary)}`}
            style={{
              top: `${startOffset}px`,
              height: `${totalHeight}px`,
              left: '2px',
              right: '2px',
              zIndex: 10,
              boxSizing: 'border-box',
            }}
            title={`${event.summary}\n${startTime} - ${endTime}`}
          >
            <div className="flex flex-col h-full justify-start overflow-hidden">
              <span className="font-bold truncate leading-tight">{event.summary}</span>
              <span className="text-[10px] opacity-80 leading-tight truncate">{timeRange}</span>
            </div>
          </div>
        </div>
      );
    });
  };


  if (loading && events.length === 0) { // Show initial load only if no events are displayed yet
    return <div className="flex justify-center items-center h-64 text-white">Loading calendar events...</div>;
  }

  // Display Firestore loading error
  if (error) {
    return (
      <div className="text-center p-6 text-yellow-400 border border-yellow-600 bg-yellow-900/50 rounded m-4">
        <h2 className="text-xl font-bold mb-2">Error loading calendar</h2>
        <p className="mb-4">{error}</p>
        <button onClick={fetchEventsFromFirestore} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Retry Fetching Events
        </button>
      </div>
    );
  }


  if (!userId && !loading) { // Ensure not loading before showing sign-in message
    return (
      <div className="text-center p-6 text-white">
        <p>Please sign in to view and sync your Google Calendar.</p>
        {/* Optionally add a sign-in button here */}
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl bg-gray-900 text-white">
      <div className="flex flex-wrap justify-between items-center p-4 border-b border-gray-700 gap-4">
        <div>
          <h2 className="text-xl font-bold">
            {/* Show loading state during Firestore fetch */}
            Google Events {loading ? '(Loading...)' : `for week of ${format(weekStart, 'MMM d, yyyy')}`}
          </h2>
          <div className="text-sm text-gray-400">Timezone: {timeZone}</div>
        </div>
        <div className="flex space-x-2 items-center">
          {/* Navigation Buttons */}
          <button onClick={goToPreviousWeek} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Previous
          </button>
          <button onClick={goToCurrentWeek} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Today
          </button>
          <button onClick={goToNextWeek} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Next
          </button>
        </div>
      </div>

      <div
        className="relative grid border border-gray-700 overflow-auto"
        style={{
          gridTemplateColumns: '70px repeat(7, 1fr)', // Time column + 7 day columns
          gridTemplateRows: '60px repeat(24, 40px)', // Header row + 24 hour rows (adjust row height if needed)
          maxHeight: '80vh' // Control overall height
        }}
      >
        {/* Timezone Header */}
        <div className="flex items-center justify-center bg-gray-800 text-xs p-1 border-b border-r border-gray-700 sticky top-0 z-20">
          {timeZone.split('/')[1]?.replace('_', ' ') || timeZone}
        </div>
        {/* Day Headers */}
        {weekDays.map((day, index) => (
          <div key={index} className="flex flex-col items-center justify-center bg-gray-800 p-1 border-b border-r border-gray-700 sticky top-0 z-20">
            <div className="text-sm">{format(day, 'EEE')}</div>
            <div className="text-2xl font-bold">{format(day, 'd')}</div>
          </div>
        ))}
        {/* Hour Labels (Sticky Left) */}
        {hours.map(hour => (
          <React.Fragment key={`time-${hour}`}>
            <div className="flex items-center justify-center bg-gray-800 text-xs border-b border-r border-gray-700 sticky left-0 z-10" style={{ gridRow: hour + 2 }}>
              {formatHourDisplay(hour)}
            </div>
          </React.Fragment>
        ))}
        {/* Grid Cells (background) */}
        {hours.map(hour =>
          weekDays.map((_, dayIndex) => (
            <div
              key={`${dayIndex}-${hour}`}
              className={`border-b border-r border-gray-700 min-h-[40px] ${hour >= 8 && hour < 18 ? 'bg-gray-800/30' : 'bg-gray-900/80'}`} // Adjust min-h if row height changes
              style={{ gridColumn: dayIndex + 2, gridRow: hour + 2 }}
            />
          ))
        )}
        {/* Render Events Overlay */}
        {renderEvents()}
      </div>
    </div>
  );
}

export default WeeklyCalendar;